import { useMutation, useQueryClient } from "@tanstack/react-query";

import { requireData } from "../../shared/api/api-error.js";
import { useApi } from "../../shared/api/api-context.js";
import type { TradingEngineClient } from "../../shared/api/client.js";
import type { components } from "../../shared/api/generated/trading-engine.js";
import { usdToMinorUnits } from "../../shared/lib/usd.js";
import { botRevisionView, type BotView } from "./models.js";
import { botKeys, settleOperation } from "./queries.js";

type BotMutation = components["schemas"]["BotMutation"];

export const filteredSizingRiskConcurrency = 3;

export interface FilteredSizingRiskSettings {
  readonly allowShort: boolean;
  readonly maxPositionNotionalUsd: string;
  readonly maxPositionQuantity: string;
  readonly positionSizingKind: "fixed-notional" | "fixed-quantity" | "percent-of-equity";
  readonly positionSizingValue: string;
}

export interface FilteredSizingRiskOutcome {
  readonly botId: string;
  readonly botName: string;
  readonly status: "failed" | "succeeded";
}

const editableStates = new Set<BotView["state"]>(["draft", "ready", "stopped", "error"]);

export function eligibleFilteredSizingRiskBots(bots: readonly BotView[]): readonly BotView[] {
  return bots.filter((bot) => editableStates.has(bot.state) && bot.activeRevisionId !== "");
}

function updateBody(
  bot: BotView,
  revision: ReturnType<typeof botRevisionView>,
  settings: FilteredSizingRiskSettings,
): BotMutation {
  const maxPositionQuantity = settings.maxPositionQuantity.trim();
  const maxPositionNotionalUsd = settings.maxPositionNotionalUsd.trim();
  return {
    configuration: {
      execution: revision.execution,
      positionSizing: {
        kind: settings.positionSizingKind,
        value: settings.positionSizingValue.trim(),
      },
      risk: {
        allowShort: settings.allowShort,
        ...(maxPositionQuantity === "" ? {} : { maxPositionQuantity }),
        ...(maxPositionNotionalUsd === ""
          ? {}
          : {
              maxPositionNotional: {
                currency: "USD",
                minorUnits: usdToMinorUnits(maxPositionNotionalUsd),
              },
            }),
      },
      schedule: revision.schedule,
      strategy: {
        ...revision.strategy,
        version: Number(revision.strategy.version),
      },
      symbol: revision.symbol,
    },
    name: bot.name,
  };
}

async function applyOne(
  api: TradingEngineClient,
  bot: BotView,
  settings: FilteredSizingRiskSettings,
): Promise<void> {
  const revisions = requireData(
    await api.GET("/revisions", {
      params: { query: { botId: bot.id, limit: 100 } },
    }),
  );
  const revision = revisions.items
    .map(botRevisionView)
    .find((candidate) => candidate.id === bot.activeRevisionId);
  if (revision === undefined) throw new Error("ACTIVE_REVISION_UNAVAILABLE");
  const accepted = requireData(
    await api.PUT("/bots/{botId}", {
      body: {
        ...updateBody(bot, revision, settings),
        expectedVersion: bot.version,
      },
      params: {
        header: { "x-idempotency-key": crypto.randomUUID() },
        path: { botId: bot.id },
      },
    }),
  );
  await settleOperation(api, accepted.operation);
}

export async function applyFilteredSizingRisk(
  api: TradingEngineClient,
  bots: readonly BotView[],
  settings: FilteredSizingRiskSettings,
  concurrency = filteredSizingRiskConcurrency,
): Promise<readonly FilteredSizingRiskOutcome[]> {
  const outcomes = new Array<FilteredSizingRiskOutcome>(bots.length);
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < bots.length) {
      const index = nextIndex;
      nextIndex += 1;
      const bot = bots[index];
      if (bot === undefined) continue;
      try {
        await applyOne(api, bot, settings);
        outcomes[index] = { botId: bot.id, botName: bot.name, status: "succeeded" };
      } catch {
        outcomes[index] = { botId: bot.id, botName: bot.name, status: "failed" };
      }
    }
  };
  const workerCount = Math.min(Math.max(1, concurrency), bots.length);
  await Promise.all(Array.from({ length: workerCount }, worker));
  return outcomes;
}

export function useApplyFilteredSizingRisk() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      bots,
      settings,
    }: {
      bots: readonly BotView[];
      settings: FilteredSizingRiskSettings;
    }) => applyFilteredSizingRisk(api, bots, settings),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: botKeys.all }),
  });
}
